import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, ArrowRight, CalendarDays, CheckCircle2, Expand, Loader2, LockKeyhole, Pause, Play, Settings2, Shrink, TimerReset, X } from 'lucide-react';
import { ApiService } from '../../../services/api';
import type { ModeConfig } from './speedReadingShared';
import { getThemeChromeButtonStyle, getThemeChromeTextStyle, ModeIllustration, normalizeMetronomeBpm } from './speedReadingShared';
import type { ReadingAssessmentSummary } from '../onboarding/steps/ReadingAssessmentStep';
import ReadingSpeedStep from '../onboarding/steps/ReadingSpeedStep';

type AssessmentPayload = {
  id: string;
  title: string;
  content: string;
  textType?: string | null;
  readingLevel?: string | null;
  recommendedReadingLevel?: string | null;
  questions: Array<{
    questionId: string;
    question: string;
    options: Record<'A' | 'B' | 'C' | 'D' | 'E', string>;
    correctOption?: 'A' | 'B' | 'C' | 'D' | 'E';
  }>;
};

type AssessmentRunPhase = 'idle' | 'reading' | 'paused' | 'questions';
type AssessmentControlSignal = {
  action: 'start' | 'pause' | 'resume' | 'finishReading' | 'completeQuestions';
  nonce: number;
};

const ASSESSMENT_OPTIONS = [
  { value: 10, readingLevel: 'İlkokul', label: 'İlkokul', hint: 'İlkokul düzeyi metinler ve sorular' },
  { value: 13, readingLevel: 'Ortaokul', label: 'Ortaokul', hint: 'Ortaokul düzeyi metinler ve sorular' },
  { value: 16, readingLevel: 'Lise', label: 'Lise', hint: 'Lise düzeyi metinler ve sorular' },
] as const;

const ASSESSMENT_FONT_SIZE_OPTIONS = [
  { value: 'small', label: 'Küçük', className: 'text-[22px] leading-[1.6]', preview: 'Aa' },
  { value: 'medium', label: 'Orta', className: 'text-[26px] leading-[1.65]', preview: 'Aa' },
  { value: 'large', label: 'Büyük', className: 'text-[30px] leading-[1.7]', preview: 'Aa' },
  { value: 'xlarge', label: 'Çok Büyük', className: 'text-[34px] leading-[1.7]', preview: 'Aa' },
] as const;

const ASSESSMENT_FONT_FAMILY_OPTIONS = [
  { value: 'GoogleSans', label: 'GoogleSans', css: 'GoogleSans, Google Sans, sans-serif' },
  { value: 'Helvetica', label: 'Helvetica', css: 'Helvetica, Arial, sans-serif' },
  { value: 'Palatino', label: 'Palatino', css: 'Palatino, Palatino Linotype, serif' },
  { value: 'OpenDyslexic', label: 'OpenDyslexic', css: 'OpenDyslexic, GoogleSans, sans-serif' },
] as const;

const ASSESSMENT_ACCENT_OPTIONS = [
  { value: '#0f4c5c', label: 'Koyu Mavi' },
  { value: '#ff1f1f', label: 'Kırmızı' },
  { value: '#12b6cf', label: 'Turkuaz' },
  { value: '#f6d32f', label: 'Sarı' },
  { value: '#e91e63', label: 'Pembe' },
  { value: '#45ad58', label: 'Yeşil' },
] as const;

const ASSESSMENT_TEXT_TYPE_OPTIONS = [
  { value: null, label: 'Tümü', hint: 'Tüm metin türleri' },
  { value: 'öykü' as const, label: 'Öykü', hint: 'Hikaye ve kurgu metinler' },
  { value: 'makale' as const, label: 'Makale', hint: 'Bilgi ve araştırma metinleri' },
];

export const ReadingAssessmentModeView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const [selectedAge, setSelectedAge] = useState<number>(13);
  const [selectedTextType, setSelectedTextType] = useState<'öykü' | 'makale' | null>(null);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [availableTexts, setAvailableTexts] = useState<any[]>([]);
  const [assessment, setAssessment] = useState<AssessmentPayload | null>(null);
  const [result, setResult] = useState<{ speed: number; assessment: ReadingAssessmentSummary | null } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadSeed, setReloadSeed] = useState(0);
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [assessmentPhase, setAssessmentPhase] = useState<AssessmentRunPhase>('idle');
  const [controlSignal, setControlSignal] = useState<AssessmentControlSignal | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [fontSize, setFontSize] = useState<(typeof ASSESSMENT_FONT_SIZE_OPTIONS)[number]['value']>('medium');
  const [fontFamily, setFontFamily] = useState<(typeof ASSESSMENT_FONT_FAMILY_OPTIONS)[number]['value']>('GoogleSans');
  const [accentColor, setAccentColor] = useState<string>('#ff1f1f');
  const [countdown, setCountdown] = useState(0);
  const [assessmentElapsedSeconds, setAssessmentElapsedSeconds] = useState(0);
  const [canContinueAssessmentQuestions, setCanContinueAssessmentQuestions] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const selectedOption = useMemo(
    () => ASSESSMENT_OPTIONS.find((option) => option.value === selectedAge) || ASSESSMENT_OPTIONS[1],
    [selectedAge]
  );
  const assessmentWordCount = useMemo(
    () => String(assessment?.content || '').trim().split(/\s+/).filter(Boolean).length,
    [assessment?.content]
  );
  const activeFontSize = ASSESSMENT_FONT_SIZE_OPTIONS.find((option) => option.value === fontSize) || ASSESSMENT_FONT_SIZE_OPTIONS[1];
  const activeFontFamily = ASSESSMENT_FONT_FAMILY_OPTIONS.find((option) => option.value === fontFamily) || ASSESSMENT_FONT_FAMILY_OPTIONS[0];
  const activeAccent = ASSESSMENT_ACCENT_OPTIONS.find((option) => option.value === accentColor) || ASSESSMENT_ACCENT_OPTIONS[1];
  const assessmentReadingFontSizeClass = {
    small: 'text-[clamp(1rem,1.75vmin,1.375rem)] leading-[1.45]',
    medium: 'text-[clamp(1.05rem,2vmin,1.625rem)] leading-[1.45]',
    large: 'text-[clamp(1.15rem,2.25vmin,1.875rem)] leading-[1.45]',
    xlarge: 'text-[clamp(1.2rem,2.45vmin,2.125rem)] leading-[1.42]',
  }[activeFontSize.value];
  const formattedAssessmentElapsedSeconds = useMemo(() => {
    const minutes = Math.floor(assessmentElapsedSeconds / 60);
    const seconds = assessmentElapsedSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [assessmentElapsedSeconds]);

  const resetAssessmentRun = useCallback(() => {
    setResult(null);
    setAssessmentStarted(false);
    setAssessmentPhase('idle');
    setControlSignal(null);
    setCountdown(0);
    setAssessmentElapsedSeconds(0);
    setCanContinueAssessmentQuestions(false);
  }, []);

  const reloadAssessment = useCallback(() => {
    resetAssessmentRun();
    setReloadSeed((prev) => prev + 1);
  }, [resetAssessmentRun]);

  const triggerAssessmentControl = useCallback((action: AssessmentControlSignal['action']) => {
    setControlSignal({ action, nonce: Date.now() });
  }, []);

  const startAssessment = useCallback(() => {
    if (loading || error || !assessment || countdown > 0) return;
    setCountdown(3);
  }, [assessment, countdown, error, loading]);

  const beginAssessmentAfterCountdown = useCallback(() => {
    setAssessmentStarted(true);
    setAssessmentPhase('reading');
    triggerAssessmentControl('start');
  }, [triggerAssessmentControl]);

  const handleAssessmentPhaseChange = useCallback((phase: 'welcome' | 'reading' | 'paused' | 'questions') => {
    setAssessmentPhase(phase === 'welcome' ? 'idle' : phase);
  }, []);

  const handleSelectAssessmentOption = useCallback((value: number) => {
    setSelectedAge(value);
    resetAssessmentRun();
  }, [resetAssessmentRun]);

  const handleSelectTextType = useCallback((value: 'öykü' | 'makale' | null) => {
    setSelectedTextType(value);
    resetAssessmentRun();
  }, [resetAssessmentRun]);

  const stepAssessmentOption = useCallback((direction: -1 | 1) => {
    const currentIndex = ASSESSMENT_OPTIONS.findIndex((option) => option.value === selectedAge);
    const safeIndex = currentIndex >= 0 ? currentIndex : 1;
    const nextIndex = Math.max(0, Math.min(ASSESSMENT_OPTIONS.length - 1, safeIndex + direction));
    handleSelectAssessmentOption(ASSESSMENT_OPTIONS[nextIndex].value);
  }, [handleSelectAssessmentOption, selectedAge]);

  const handleToggleFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }

      await containerRef.current?.requestFullscreen();
    } catch (fullscreenError) {
      console.error('Tam ekran değiştirilemedi:', fullscreenError);
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (countdown <= 0) return;

    const timer = window.setTimeout(() => {
      if (countdown <= 1) {
        setCountdown(0);
        beginAssessmentAfterCountdown();
        return;
      }

      setCountdown((prev) => prev - 1);
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [beginAssessmentAfterCountdown, countdown]);

  useEffect(() => {
    setSelectedTextId(null);
    let active = true;
    const fetchTexts = async () => {
      try {
        const query = new URLSearchParams({
          difficultyLevel: selectedOption.readingLevel,
        });
        if (selectedTextType) {
          query.set('textType', selectedTextType);
        }
        const response = await ApiService.getApi(`speed-reading/content/library?${query.toString()}`);
        if (!active) return;
        setAvailableTexts(response.items || []);
      } catch (err) {
        console.error('Available texts could not be loaded:', err);
      }
    };
    void fetchTexts();
    return () => {
      active = false;
    };
  }, [selectedOption.readingLevel, selectedTextType]);

  useEffect(() => {
    let active = true;

    const loadAssessment = async () => {
      try {
        setLoading(true);
        setError(null);
        const query = new URLSearchParams({
          age: String(selectedAge),
          readingLevel: selectedOption.readingLevel,
        });
        if (selectedTextType) query.set('textType', selectedTextType);
        if (selectedTextId) query.set('contentId', selectedTextId);
        const response = await ApiService.getApi(`speed-reading/onboarding/assessment?${query.toString()}`);
        if (!active) return;
        setAssessment(response as AssessmentPayload);
      } catch (loadError) {
        if (!active) return;
        console.error('Okuma anlama seviye testi yüklenemedi:', loadError);
        setAssessment(null);
        setError('Seviye testi şu anda yüklenemedi. Lütfen tekrar deneyin.');
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadAssessment();

    return () => {
      active = false;
    };
  }, [reloadSeed, selectedAge, selectedOption.readingLevel, selectedTextType, selectedTextId]);

  return (
    <div ref={containerRef} className={`${isFullscreen ? 'h-screen p-2' : 'h-full min-h-0 p-3 lg:p-4'} overflow-hidden bg-[#f3f5f7]`}>
      <div className={`${isFullscreen ? 'max-w-none' : 'max-w-[1260px]'} mx-auto h-full min-h-0`}>
        <div className="relative flex h-full min-h-0 flex-col gap-3 overflow-hidden rounded-xl border border-[#e6edf1] bg-[#f8fafb] px-4 py-3 lg:flex-row lg:gap-4 lg:px-5 lg:py-4">
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="mb-3 flex shrink-0 items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-[19px] w-[19px]" style={getThemeChromeTextStyle()} />
                <div className="text-[18px] font-bold leading-tight" style={getThemeChromeTextStyle()}>{mode.name}</div>
              </div>
              <div className="flex items-center gap-3 text-[#afbbc2]">
                <button onClick={() => setSettingsOpen(true)} className="hover:text-[#6b7f8a]" title="Egzersiz Ayarları">
                  <Settings2 className="h-4 w-4" />
                </button>
                <button onClick={() => { void handleToggleFullscreen(); }} className="hover:text-[#6b7f8a]" title={isFullscreen ? 'Tam Ekrandan Çık' : 'Tam Ekran'}>
                  {isFullscreen ? <Shrink className="h-4 w-4" /> : <Expand className="h-4 w-4" />}
                </button>
                <button onClick={onClose} className="hover:text-[#6b7f8a]">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="relative min-h-0 flex-1 overflow-hidden rounded-[10px] border border-[#e8edf0] bg-white px-4 py-4 lg:px-5">
              <div className="h-full min-h-0 overflow-hidden">
                {result ? (
                  <div className="flex min-h-full items-center justify-center">
                    <div className="w-full max-w-3xl rounded-[28px] border border-[#dce8ee] bg-[#fbfdfe] p-8 shadow-[0_18px_50px_rgba(26,72,92,0.06)]">
                      <div className="flex items-center gap-3" style={getThemeChromeTextStyle()}>
                        <CheckCircle2 className="h-7 w-7" />
                        <div className="text-2xl font-black">Değerlendirme Tamamlandı</div>
                      </div>
                      <div className="mt-6 grid gap-4 md:grid-cols-3">
                        <div className="rounded-2xl border border-[#e5edf1] bg-white p-5">
                          <div className="text-xs font-semibold text-[#7a8e98]">Seviye</div>
                          <div className="mt-2 text-2xl font-black text-[#18485c]">
                            {ASSESSMENT_OPTIONS.find((option) => option.value === result.assessment?.age)?.label || '7 ve 8. Sınıflar'}
                          </div>
                        </div>
                        <div className="rounded-2xl border border-[#e5edf1] bg-white p-5">
                          <div className="text-xs font-semibold text-[#7a8e98]">Anlama Oranı</div>
                          <div className="mt-2 text-2xl font-black text-[#18485c]">%{result.assessment?.score || 0}</div>
                        </div>
                        <div className="rounded-2xl border border-[#e5edf1] bg-white p-5">
                          <div className="text-xs font-semibold text-[#7a8e98]">Kelime Sayısı</div>
                          <div className="mt-2 text-2xl font-black text-[#18485c]">{assessmentWordCount || 0}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto flex h-full w-full max-w-5xl flex-col">
                    {loading ? (
                      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-4 rounded-[28px] border border-[#e1ebef] bg-[#fbfdfe]">
                        <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--theme-primary)' }} />
                        <p className="text-sm text-[#718792]">Yaşa uygun metin hazırlanıyor...</p>
                      </div>
                    ) : error ? (
                      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-4 rounded-[28px] border border-[#e1ebef] bg-[#fbfdfe] px-6 text-center">
                        <p className="text-base font-semibold text-[#355c68]">{error}</p>
                        <button
                          onClick={reloadAssessment}
                          className="rounded-xl px-5 py-2.5 font-semibold text-white"
                          style={getThemeChromeButtonStyle()}
                        >
                          Tekrar Dene
                        </button>
                      </div>
                    ) : countdown > 0 ? (
                      <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center px-8 text-center">
                        <div className="text-[clamp(4rem,12vmin,5.375rem)] font-black leading-none text-[#18485c]" style={{ fontFamily: activeFontFamily.css }}>
                          {countdown}
                        </div>
                      </div>
                    ) : assessmentStarted ? (
                      <ReadingSpeedStep
                        key={`${assessment?.id || 'assessment'}-${reloadSeed}`}
                        initialSpeed={220}
                        onSpeedChange={() => { }}
                        onNext={(payload) => {
                          setResult({
                            speed: payload.speed,
                            assessment: payload.assessment || null,
                          });
                        }}
                        readingTitle={assessment?.title}
                        sourceText={assessment?.content}
                        sourceQuestions={assessment?.questions}
                        difficultyLevel={assessment?.recommendedReadingLevel || assessment?.readingLevel || selectedOption.readingLevel}
                        sourceTextLoading={loading}
                        startImmediately
                        hideWelcome
                        hideReadingActions
                        hideQuestionActions
                        hideReadingHeader
                        externalControl={controlSignal}
                        onPhaseChange={handleAssessmentPhaseChange}
                        onElapsedSecondsChange={setAssessmentElapsedSeconds}
                        onQuestionReadinessChange={setCanContinueAssessmentQuestions}
                        fontSizeClass={assessmentReadingFontSizeClass}
                        fontFamily={activeFontFamily.css}
                        accentColor={accentColor}
                        fitContainer
                      />
                    ) : (
                      <div className="flex h-full min-h-0 flex-1 items-center justify-center px-4 text-center sm:px-8 lg:px-10">
                        <div className="max-w-4xl">
                          <p className="text-[clamp(1.05rem,2.25vmin,1.5rem)] font-black leading-[1.45] text-[#285f73]">
                            Metni normal okuma hızınızla okuyun; süre, metin başladıktan sonra otomatik olarak ölçülür.
                            Metni bitirdiğinizde anlama sorularına geçerek hem okuma hızınızı hem de anlama oranınızı birlikte görebilirsiniz.
                          </p>
                          <p className="mt-[clamp(1rem,4vmin,2.25rem)] text-[clamp(1rem,2vmin,1.375rem)] font-black leading-[1.45] text-[#285f73]">
                            Seviye, yazı boyutu ve vurgu rengi ayarlarını üstteki ayarlar simgesinden değiştirebilir;
                            farklı bir metinle ölçüm yapmak için sağ paneldeki Yeni Metin seçeneğini kullanabilirsiniz.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <aside className="flex min-h-0 w-full shrink-0 flex-col rounded-xl border border-[#e0eaee] bg-white p-3 lg:h-full lg:w-[300px] lg:p-4 xl:w-[320px]">
            <div className="grid min-h-0 flex-1 gap-3 overflow-hidden sm:grid-cols-2 lg:flex lg:flex-col lg:gap-0">
              <div className="flex flex-col items-center text-center lg:mt-1">
                <div className="hidden rounded-[1.5rem] bg-[#f7fafc] px-4 py-3 lg:block">
                  <ModeIllustration mode={mode} />
                </div>
                <h3 className="text-[22px] font-black leading-none lg:mt-3 lg:text-[26px] xl:text-[30px]" style={getThemeChromeTextStyle()}>{mode.name}</h3>
                <p className="mt-2 text-[12px] leading-5 text-[#69808b] lg:text-[13px] lg:leading-6">
                  Yaşa uygun metinle okuma anlama düzeyini ölçün.
                </p>
              </div>

              <div className="rounded-xl border border-[#e4edf1] bg-[#f8fbfc] p-3 lg:mt-3 lg:p-4">
                <div className="text-xs font-semibold text-[#6c8591]">Seçili Seviye</div>
                <div className="mt-2 text-sm font-black text-[#27596a]">{selectedOption.label}</div>
                <p className="mt-1 text-[12px] leading-5 text-[#6f8490] lg:mt-2">{selectedOption.hint}</p>
              </div>

              <div className="rounded-xl border border-[#e4edf1] bg-[#f8fbfc] p-3 lg:mt-3 lg:p-4">
                <div className="text-xs font-semibold text-[#6c8591] mb-2">Metin Türü</div>
                <div className="flex gap-2">
                  {ASSESSMENT_TEXT_TYPE_OPTIONS.map((option) => {
                    const isActive = selectedTextType === option.value;
                    return (
                      <button
                        key={String(option.value)}
                        onClick={() => handleSelectTextType(option.value)}
                        className="flex-1 rounded-lg px-2 py-1.5 text-xs font-black transition-all"
                        style={{
                          backgroundColor: isActive ? 'color-mix(in srgb, var(--theme-primary) 12%, white)' : '#fff',
                          color: isActive ? 'var(--theme-primary)' : '#55707d',
                          border: isActive ? '1.5px solid color-mix(in srgb, var(--theme-primary) 30%, white)' : '1.5px solid #e4edf1',
                        }}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-xl border border-[#e4edf1] bg-[#f8fbfc] p-3 sm:col-span-2 lg:col-span-1 lg:mt-3 lg:p-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-semibold text-[#6c8591]">Aktif Metin</div>
                  <button
                    onClick={reloadAssessment}
                    disabled={loading}
                    className="rounded-full border border-[#d9e3e8] bg-white px-2.5 py-1 text-[10px] font-bold text-[#55707d] transition-colors hover:bg-[#f7fbfd] disabled:opacity-50"
                  >
                    Yeni Metin
                  </button>
                </div>
                <div className="mt-2 max-h-10 overflow-hidden text-sm font-black leading-5 text-[#27596a]">{assessment?.title || 'Metin hazırlanıyor'}</div>
                <div className="mt-2 flex flex-wrap gap-2 lg:mt-3">
                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#5b7784]">
                    {assessmentWordCount || 0} kelime
                  </span>
                  <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#5b7784]">
                    {assessment?.questions?.length || 0} soru
                  </span>
                  {(assessment?.textType || assessment?.readingLevel) && (
                    <span className="rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-[#5b7784]">
                      {assessment?.textType || assessment?.readingLevel}
                    </span>
                  )}
                </div>
              </div>

              {result && (
                <div className="rounded-xl border border-[#dceaf0] bg-[#f7fbfd] p-3 sm:col-span-2 lg:col-span-1 lg:mt-3 lg:p-4">
                  <div className="text-xs font-semibold text-[#6c8591]">Sonuç</div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-white px-3 py-2">
                      <div className="text-[10px] font-semibold text-[#7b909b]">Anlama</div>
                      <div className="mt-1 text-lg font-black text-[#194e62]">%{result.assessment?.score || 0}</div>
                    </div>
                    <div className="rounded-lg bg-white px-3 py-2">
                      <div className="text-[10px] font-semibold text-[#7b909b]">Hız</div>
                      <div className="mt-1 text-lg font-black text-[#194e62]">{result.speed}</div>
                    </div>
                  </div>
                </div>
              )}

            </div>

            <div className="shrink-0 pt-3 lg:pt-4">
              {!result && (
                <div className="mb-3 lg:mb-4">
                  <p className="text-xs text-[#98a8b1]">Geçen Süre</p>
                  <p className="mt-1 text-[36px] font-black leading-none lg:text-[40px] xl:text-[44px]" style={getThemeChromeTextStyle()}>
                    {formattedAssessmentElapsedSeconds}
                  </p>
                  <p className="mt-2 text-xs text-[#7d8f98]">
                    {assessmentPhase === 'paused'
                      ? 'Duraklatıldı'
                      : assessmentPhase === 'questions'
                        ? 'Okuma tamamlandı'
                        : 'Okuma süresi'}
                  </p>
                </div>
              )}

              {!result && assessmentPhase === 'idle' && (
                <button
                  onClick={startAssessment}
                  disabled={loading || Boolean(error) || !assessment || countdown > 0}
                  className="h-12 w-full rounded-lg text-base font-bold text-white transition-colors disabled:opacity-50"
                  style={getThemeChromeButtonStyle()}
                >
                  {countdown > 0 ? `${countdown}` : 'Başlat'}
                </button>
              )}

              {!result && assessmentPhase === 'reading' && (
                <>
                  <button
                    onClick={() => triggerAssessmentControl('pause')}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-bold text-white transition-colors"
                    style={getThemeChromeButtonStyle()}
                  >
                    <Pause className="h-4 w-4" />
                    Durdur
                  </button>
                  <button
                    onClick={() => triggerAssessmentControl('finishReading')}
                    className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#24465a] text-base font-bold text-white transition-colors hover:bg-[#1d3a4b]"
                  >
                    Metni Bitirdim
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </>
              )}

              {!result && assessmentPhase === 'paused' && (
                <>
                  <button
                    onClick={() => triggerAssessmentControl('resume')}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-bold text-white transition-colors"
                    style={getThemeChromeButtonStyle()}
                  >
                    <Play className="h-4 w-4" />
                    Başlat
                  </button>
                  <button
                    onClick={() => triggerAssessmentControl('finishReading')}
                    className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#24465a] text-base font-bold text-white transition-colors hover:bg-[#1d3a4b]"
                  >
                    Metni Bitirdim
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </>
              )}

              {!result && assessmentPhase === 'questions' && (
                <button
                  onClick={() => triggerAssessmentControl('completeQuestions')}
                  disabled={!canContinueAssessmentQuestions}
                  className="flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-bold text-white transition-colors disabled:opacity-50"
                  style={getThemeChromeButtonStyle()}
                >
                  TAMAMLA
                </button>
              )}

              {result && (
                <>
                  <button
                    onClick={reloadAssessment}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#24465a] text-base font-bold text-white transition-colors hover:bg-[#1d3a4b] mb-3"
                  >
                    <TimerReset className="h-4 w-4" />
                    Tekrar Ölç
                  </button>
                  <button
                    onClick={onClose}
                    className="flex h-12 w-full items-center justify-center gap-2 rounded-lg text-base font-bold text-white transition-colors"
                    style={getThemeChromeButtonStyle()}
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Geri Dön
                  </button>
                </>
              )}
            </div>
          </aside>

          {settingsOpen && (
            <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-6 backdrop-blur-[2px]">
              <div className="w-full max-w-[980px] rounded-[10px] bg-white px-7 py-6 shadow-[0_22px_55px_rgba(15,35,48,0.28)]">
                <div className="mb-6 flex items-center justify-between">
                  <h4 className="text-[22px] font-black text-[#24586a]">Egzersiz Ayarları</h4>
                  <button className="text-[#8aa0aa] transition-colors hover:text-[#455e69]" onClick={() => setSettingsOpen(false)}>
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid gap-7 md:grid-cols-[1fr_1px_1.05fr]">
                  <div className="space-y-4">
                    <div className="grid h-14 grid-cols-[1fr_58px_120px_58px] overflow-hidden rounded-xl border border-[#dbe8ee] bg-white">
                      <div className="flex items-center px-5 text-[13px] font-black uppercase tracking-[0.08em] text-[#9aabb4]">
                        Ölçüm Seviyesi
                      </div>
                      <button
                        onClick={() => stepAssessmentOption(-1)}
                        className="border-l border-[#edf3f6] text-xl font-black text-[#9aabb4] transition-colors hover:bg-[#f7fbfd] hover:text-[#55707d]"
                      >
                        «
                      </button>
                      <div className="flex items-center justify-center border-l border-[#edf3f6] text-sm font-black text-[#55707d]">
                        {selectedOption.label}
                      </div>
                      <button
                        onClick={() => stepAssessmentOption(1)}
                        className="border-l border-[#edf3f6] text-xl font-black text-[#9aabb4] transition-colors hover:bg-[#f7fbfd] hover:text-[#55707d]"
                      >
                        »
                      </button>
                    </div>

                    <div className="relative grid h-14 grid-cols-[140px_1fr] overflow-hidden rounded-xl border border-[#dbe8ee] bg-white">
                      <div className="flex items-center px-5 text-[13px] font-black uppercase tracking-[0.08em] text-[#9aabb4] border-r border-[#edf3f6]">
                        Aktif Metin
                      </div>
                      <select
                        value={selectedTextId || ''}
                        onChange={(e) => {
                          const val = e.target.value;
                          setSelectedTextId(val || null);
                          resetAssessmentRun();
                        }}
                        disabled={loading}
                        className="w-full h-full px-4 text-sm font-black text-[#55707d] bg-transparent outline-none cursor-pointer disabled:opacity-50 appearance-none"
                      >
                        <option value="">[Rastgele Seçim]</option>
                        {availableTexts.map((txt) => (
                          <option key={txt.id} value={txt.id}>
                            {txt.title}
                          </option>
                        ))}
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none text-[#55707d] font-black">
                        ▼
                      </div>
                    </div>

                    <div className="grid h-14 overflow-hidden rounded-xl border border-[#dbe8ee] bg-white" style={{ gridTemplateColumns: '1fr repeat(3, auto)' }}>
                      <div className="flex items-center px-5 text-[13px] font-black uppercase tracking-[0.08em] text-[#9aabb4]">
                        Metin Türü
                      </div>
                      {ASSESSMENT_TEXT_TYPE_OPTIONS.map((option) => {
                        const isActive = selectedTextType === option.value;
                        return (
                          <button
                            key={String(option.value)}
                            onClick={() => handleSelectTextType(option.value)}
                            className="border-l border-[#edf3f6] px-4 text-sm font-black transition-colors"
                            style={{
                              backgroundColor: isActive ? 'color-mix(in srgb, var(--theme-primary) 9%, white)' : undefined,
                              color: isActive ? 'var(--theme-primary)' : '#55707d',
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <div>
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-sm font-black text-[#24586a]">Seviye Seçimi</div>
                        <span className="rounded bg-[#fff0e7] px-2.5 py-1 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>
                          {selectedOption.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        {ASSESSMENT_OPTIONS.map((option) => {
                          const isActive = selectedAge === option.value;
                          return (
                            <button
                              key={option.value}
                              onClick={() => handleSelectAssessmentOption(option.value)}
                              className="h-[76px] rounded-lg border bg-white px-4 text-left transition-all"
                              style={{
                                borderColor: isActive ? 'var(--theme-primary)' : '#dfeaf0',
                                backgroundColor: isActive ? 'color-mix(in srgb, var(--theme-primary) 9%, white)' : '#fff',
                                color: isActive ? 'var(--theme-primary)' : '#55707d',
                              }}
                            >
                              <div className="text-sm font-black">{option.label}</div>
                              <div className="mt-1 text-[11px] font-semibold text-[#7b909a]">{option.hint}</div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="hidden bg-[#edf3f6] md:block" />

                  <div>
                    <div className="mb-4 flex items-center justify-between">
                      <div className="text-sm font-black text-[#24586a]">Yazı Boyutu</div>
                      <span className="rounded bg-[#fff0e7] px-2.5 py-1 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>
                        {activeFontSize.label}
                      </span>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {ASSESSMENT_FONT_SIZE_OPTIONS.map((option) => {
                        const isActive = fontSize === option.value;
                        return (
                          <button
                            key={option.value}
                            onClick={() => setFontSize(option.value)}
                            className="h-11 rounded-lg border bg-white text-center font-black transition-all"
                            style={{
                              borderColor: isActive ? 'var(--theme-primary)' : '#dfeaf0',
                              backgroundColor: isActive ? 'color-mix(in srgb, var(--theme-primary) 9%, white)' : '#fff',
                              color: isActive ? 'var(--theme-primary)' : '#55707d',
                              fontSize: option.value === 'small' ? 13 : option.value === 'medium' ? 16 : option.value === 'large' ? 19 : 22,
                            }}
                          >
                            {option.preview}
                          </button>
                        );
                      })}
                    </div>

                    <div className="mt-6">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-sm font-black text-[#24586a]">Yazı Karakteri</div>
                        <span className="rounded bg-[#fff0e7] px-2.5 py-1 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>
                          {activeFontFamily.label}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        {ASSESSMENT_FONT_FAMILY_OPTIONS.map((option) => {
                          const isActive = fontFamily === option.value;
                          return (
                            <button
                              key={option.value}
                              onClick={() => setFontFamily(option.value)}
                              className="h-11 rounded-lg border bg-white text-sm font-bold transition-all"
                              style={{
                                borderColor: isActive ? 'var(--theme-primary)' : '#dfeaf0',
                                backgroundColor: isActive ? 'color-mix(in srgb, var(--theme-primary) 9%, white)' : '#fff',
                                color: isActive ? 'var(--theme-primary)' : '#55707d',
                                fontFamily: option.css,
                              }}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-6">
                      <div className="mb-3 flex items-center justify-between">
                        <div className="text-sm font-black text-[#24586a]">Vurgu Rengi</div>
                        <span className="rounded bg-[#fff0e7] px-2.5 py-1 text-xs font-bold" style={{ color: 'var(--theme-primary)' }}>
                          {activeAccent.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-3">
                        {ASSESSMENT_ACCENT_OPTIONS.map((option) => {
                          const isActive = accentColor === option.value;
                          return (
                            <button
                              key={option.value}
                              onClick={() => setAccentColor(option.value)}
                              className="h-8 w-8 rounded-full border-2 transition-all"
                              style={{
                                backgroundColor: option.value,
                                borderColor: isActive ? '#fff' : '#e2edf2',
                                boxShadow: isActive ? `0 0 0 3px ${option.value}` : 'none',
                              }}
                              title={option.label}
                            />
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type HomeworkPlanResponse = {
  readingLevel: string;
  days: number;
  totalDays?: number;
  totalWeeks?: number;
  startDate?: string;
  today?: string;
  currentDay?: number;
  items: Array<{
    day: number;
    week?: number;
    availableDate?: string;
    locked?: boolean;
    status?: 'available' | 'today' | 'locked' | 'completed';
    completed?: boolean;
    focus: string;
    tasks: Array<{
      taskId?: string;
      sectionId: string;
      exerciseId: string;
      title?: string;
      durationMinutes: number;
      difficultyLevel?: string;
      completed?: boolean;
      completedAt?: string | null;
      status?: 'pending' | 'completed';
      settings?: {
        metronomeEnabled?: boolean;
        metronomeBpm?: number;
        stopwatchEnabled?: boolean;
        wordsPerMinute?: number;
      };
    }>;
  }>;
};

const TASK_LABELS: Record<string, string> = {
  '13-nokta-goz-egzersizi': '13 Nokta',
  'goz-egzersizleri-kolonlar': 'Kolonlar',
  takistoskop: 'Takistoskop',
  golgeleme: 'Gölgeli Metin',
  'multi-fokus': 'Multi Fokus',
  'zit-anlam': 'Zıt Anlam',
  'metin-okuma': 'Durağan Metin',
  'kaybolan-metin': 'Kaybolan Metin',
  'sihirli-hiz': 'Sihirli Hız',
  'eksiltili-metin': 'Eksiltili Metin',
  'bir-bakista-gorelim': 'Bir Bakışta Görelim',
  'somut-anlam': 'Somut Anlam',
  'metin-arama': 'Metin Arama',
  'gozlerimizi-sektirerek-gorelim': 'Gözlerimizi Sektirerek Görelim',
  'anlama-calismalari': 'Anlamaya Yönelik Çalışmalar',
  'serbest-okuma-modu': 'Serbest Okuma',
};

const HOMEWORK_PLAN_DAYS = 35;

const formatPlanDate = (dateString?: string) => {
  if (!dateString) return '';

  try {
    return new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long' }).format(new Date(`${dateString}T00:00:00+03:00`));
  } catch {
    return dateString;
  }
};

export const HomeworkPlanView: React.FC<{
  mode: ModeConfig;
  onClose: () => void;
}> = ({ mode, onClose }) => {
  const [plan, setPlan] = useState<HomeworkPlanResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [togglingTaskIds, setTogglingTaskIds] = useState<string[]>([]);

  useEffect(() => {
    let active = true;

    const loadPlan = async () => {
      try {
        setLoading(true);
        const response = await ApiService.getApi(`speed-reading/program/homework-plan?days=${HOMEWORK_PLAN_DAYS}`);
        if (active) setPlan(response as HomeworkPlanResponse);
      } catch (error) {
        console.error('Günlük çalışma planı yüklenemedi:', error);
        if (active) setPlan(null);
      } finally {
        if (active) setLoading(false);
      }
    };

    void loadPlan();
    return () => {
      active = false;
    };
  }, []);

  const weekGroups = useMemo(() => {
    const items = plan?.items || [];

    return Array.from({ length: plan?.totalWeeks || 5 }, (_, index) => {
      const week = index + 1;
      return {
        week,
        items: items.filter((item) => (item.week || Math.floor((item.day - 1) / 7) + 1) === week),
      };
    }).filter((group) => group.items.length > 0);
  }, [plan?.items, plan?.totalWeeks]);

  const toggleHomeworkTask = useCallback(async (taskId?: string) => {
    if (!taskId || togglingTaskIds.includes(taskId)) return;

    setTogglingTaskIds((prev) => [...prev, taskId]);
    try {
      const response = await ApiService.patchApi(`speed-reading/program/homework-plan/tasks/${encodeURIComponent(taskId)}/toggle`, {});
      const nextTask = response?.task;

      setPlan((current) => {
        if (!current) return current;

        const nextItems = current.items.map((dayItem) => {
          const tasks = dayItem.tasks.map((task, index) => {
            const currentTaskId = task.taskId || `${dayItem.day}-${task.exerciseId}-${index}`;
            if (currentTaskId !== taskId) return task;

            return {
              ...task,
              completed: Boolean(nextTask?.completed),
              completedAt: nextTask?.completedAt || null,
              status: nextTask?.status || (nextTask?.completed ? 'completed' : 'pending'),
            };
          });
          const completed = tasks.length > 0 && tasks.every((task) => Boolean(task.completed));

          return {
            ...dayItem,
            tasks,
            completed,
            status: completed ? 'completed' as const : dayItem.status === 'completed' ? 'available' as const : dayItem.status,
          };
        });

        return {
          ...current,
          items: nextItems,
        };
      });
    } catch (error) {
      console.error('Ödev tamamlanma durumu güncellenemedi:', error);
    } finally {
      setTogglingTaskIds((prev) => prev.filter((id) => id !== taskId));
    }
  }, [togglingTaskIds]);

  return (
    <div className="h-full p-6 bg-[#f3f5f7]">
      <div className="mx-auto flex h-full min-h-0 max-w-[1240px] flex-col rounded-xl border border-[#e6edf1] bg-[#f8fafb] px-6 py-5">
        <div className="mb-6 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="rounded-[2rem] bg-[#f7fafc] px-5 py-4">
              <ModeIllustration mode={mode} />
            </div>
            <div>
              <div className="text-[24px] font-black" style={getThemeChromeTextStyle()}>{mode.name}</div>
              <p className="mt-1 text-sm text-[#67808c]">5 haftalık günlük çalışma akışınızı buradan takip edin.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="rounded-xl border border-[#d8e4e9] bg-white px-4 py-2 text-sm font-bold text-[#57707b]">
              5 Hafta / 35 Gün
            </div>
            <button onClick={onClose} className="text-[#8397a1] hover:text-[#445a63]">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex h-[60vh] flex-col items-center justify-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: 'var(--theme-primary)' }} />
            <p className="text-sm font-semibold text-[#5d7681]">Çalışma planı hazırlanıyor...</p>
          </div>
        ) : !plan ? (
          <div className="flex h-[60vh] flex-col items-center justify-center gap-4 text-center">
            <CalendarDays className="h-10 w-10 text-[#aab8bf]" />
            <p className="text-sm font-semibold text-[#5d7681]">Plan şu anda alınamadı.</p>
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="mb-5 shrink-0 rounded-2xl border border-[#dde9ee] bg-white px-5 py-4">
              <div className="text-xs font-semibold text-[#7a8e98]">Önerilen Seviye</div>
              <div className="mt-2 flex flex-wrap items-end justify-between gap-3">
                <div className="text-2xl font-black text-[#18485c]">{plan.readingLevel}</div>
                <div className="text-sm font-semibold text-[#617987]">
                  Bugün: Gün {plan.currentDay || 1}
                </div>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto pr-2">
              <div className="space-y-6">
                {weekGroups.map((weekGroup) => (
                  <section key={weekGroup.week}>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-black uppercase text-[#617987]">{weekGroup.week}. Hafta</h3>
                      <div className="h-px flex-1 bg-[#e3edf1] ml-4" />
                    </div>
                    <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
                      {weekGroup.items.map((dayItem) => {
                        const locked = Boolean(dayItem.locked);
                        const isToday = dayItem.status === 'today';
                        const isDayCompleted = Boolean(dayItem.completed) || dayItem.status === 'completed';

                        return (
                          <div
                            key={dayItem.day}
                            className={`rounded-2xl border p-5 transition-colors ${locked ? 'border-[#e7edf1] bg-[#f3f6f8] opacity-75' : isDayCompleted ? 'border-[#cfe7dc] bg-[#fbfefd]' : 'border-[#e1ebef] bg-white'}`}
                          >
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <div className="text-xs font-semibold text-[#7a8e98]">Gün {dayItem.day}</div>
                                <div className={`mt-1 text-lg font-black ${locked ? 'text-[#8a9ca5]' : 'text-[#1f4e5f]'}`}>{dayItem.focus}</div>
                                <div className="mt-1 text-xs font-semibold text-[#8ba0aa]">{formatPlanDate(dayItem.availableDate)}</div>
                              </div>
                              {locked ? (
                                <LockKeyhole className="h-5 w-5 text-[#9aaab2]" />
                              ) : isDayCompleted ? (
                                <CheckCircle2 className="h-5 w-5 text-[#5c9b76]" />
                              ) : (
                                <CalendarDays className={`h-5 w-5 ${isToday ? 'text-[#ff7a2f]' : 'text-[#7ea18d]'}`} />
                              )}
                            </div>

                            {locked ? (
                              <div className="mt-4 rounded-xl border border-dashed border-[#d8e2e7] bg-white/70 px-4 py-4 text-sm font-semibold text-[#7d919a]">
                                Bu ödev {formatPlanDate(dayItem.availableDate)} tarihinde açılacak.
                              </div>
                            ) : (
                              <div className="mt-4 space-y-3">
                                {dayItem.tasks.map((task, index) => {
                                  const taskId = task.taskId || `${dayItem.day}-${task.exerciseId}-${index}`;
                                  const isCompleted = Boolean(task.completed) || task.status === 'completed';
                                  const isToggling = togglingTaskIds.includes(taskId);
                                  const taskLabel = task.title || TASK_LABELS[task.exerciseId] || task.exerciseId;

                                  return (
                                    <button
                                      key={taskId}
                                      type="button"
                                      disabled={isToggling}
                                      onClick={() => void toggleHomeworkTask(task.taskId)}
                                      className={`w-full cursor-pointer rounded-xl border px-4 py-3 text-left transition-all hover:-translate-y-0.5 hover:shadow-sm focus:outline-none focus:ring-2 focus:ring-[#9dc8d7] disabled:cursor-wait disabled:opacity-70 ${isCompleted ? 'border-[#cfe7dc] bg-[#f6fbf8] hover:border-[#8fc3a5] hover:bg-[#eef8f2]' : 'border-[#edf3f5] bg-[#fbfdfe] hover:border-[#bdd3dc] hover:bg-white'}`}
                                      title={isCompleted ? 'Tamamlandı işaretini kaldır' : 'Tamamlandı olarak işaretle'}
                                    >
                                      <div className="flex items-start justify-between gap-3">
                                        <div>
                                          <div className={`text-sm font-bold ${isCompleted ? 'text-[#47745b]' : 'text-[#2a5869]'}`}>{taskLabel}</div>
                                          <div className="mt-1 text-xs text-[#69808b]">{task.durationMinutes} dakika</div>
                                        </div>
                                        {isCompleted ? (
                                          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-[#5c9b76]" />
                                        ) : task.difficultyLevel ? (
                                          <span className="rounded-full bg-[#eef5f3] px-2.5 py-1 text-[11px] font-semibold text-[#557a6d]">
                                            {task.difficultyLevel}
                                          </span>
                                        ) : null}
                                      </div>
                                      {task.settings ? (
                                        <div className="mt-3 flex flex-wrap gap-2 text-[11px] font-semibold text-[#5e7781]">
                                          {typeof task.settings.wordsPerMinute === 'number' ? <span>{task.settings.wordsPerMinute} WPM</span> : null}
                                          {task.settings.metronomeEnabled ? <span>Metronom {normalizeMetronomeBpm(task.settings.metronomeBpm)} BPM</span> : null}
                                          {task.settings.stopwatchEnabled ? <span>Kronometre açık</span> : null}
                                        </div>
                                      ) : null}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
